import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import UrgencyTag from "../urgencies/UrgencyTag";
import { Ticket } from "../tickets/Ticket";
import { routes } from "../../common/routing/routes";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import { selectAuthUser } from "../auth/authSlice";
import {
  defaultPageSize,
  defaultPaginationOptions,
  emptyPaginationDetails,
  getPaginationFirst,
  getPaginationOptionsFromDataTable,
} from "../../common/api/paginationHelper";
import TicketStatusButtons from "../ticketStatuses/TicketStatusButtons";
import { searchAllTickets, searchOwnTickets } from "../tickets/ticketsApi";
import { inWorkStatusesIds } from "../ticketStatuses/TicketStatus";
import LoadingSpinner from "../../common/components/loadingSpinner/LoadingSpinner";
import { PropsWithClassName } from "../../common/types/PropsWithClassName";

interface UserTicketsCardProps extends PropsWithClassName {
  userId?: string;
}

export default function UserTicketsCard({ userId, className }: UserTicketsCardProps) {
  const navigate = useNavigate();
  const authUser = useSelector(selectAuthUser);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pagination, setPagination] = useState(emptyPaginationDetails);
  const [first, setFirst] = useState(getPaginationFirst(pagination.pageNumber, defaultPageSize));
  const [rows, setRows] = useState(defaultPageSize);
  const [selectedTicketStatuses, setSelectedTicketStatuses] = useState<string[] | undefined>(inWorkStatusesIds);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const doFetchTickets = async () => {
      setIsLoading(true);
      const response = userId
        ? await searchAllTickets({
            ownerId: userId,
            statuses: selectedTicketStatuses,
            sortParameters: [
              {
                columnName: "Created",
                isDescending: true,
              },
            ],
            ...getPaginationOptionsFromDataTable(first, rows),
          })
        : await searchOwnTickets({
            ownerId: authUser!.id,
            statuses: selectedTicketStatuses,
            sortParameters: [
              {
                columnName: "Created",
                isDescending: true,
              },
            ],
            ...getPaginationOptionsFromDataTable(first, rows),
          });
      setPagination(response.pagination);
      setTickets(response.tickets);
      setIsLoading(false);
    };
    doFetchTickets();
  }, [first, rows, selectedTicketStatuses]);

  const resetPagination = () => {
    setPagination(emptyPaginationDetails);
    setFirst(getPaginationFirst(emptyPaginationDetails.pageNumber, defaultPageSize));
    setRows(defaultPageSize);
  };

  return (
    <Card
      className={`${className}`}
      title={
        <div className="flex align-items-center justify-content-between">
          <div className="text-2xl font-bold">Tichete</div>
          <TicketStatusButtons
            inline
            value={selectedTicketStatuses}
            onChange={(ticketStatuses) => {
              setSelectedTicketStatuses(ticketStatuses);
              resetPagination();
            }}
          ></TicketStatusButtons>
        </div>
      }
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <DataTable
          size="small"
          lazy
          paginator
          first={first}
          rows={rows}
          rowsPerPageOptions={defaultPaginationOptions}
          totalRecords={pagination.totalCount}
          value={tickets}
          emptyMessage={<NoResultsFound />}
          rowClassName={() => "cursor-pointer"}
          onRowClick={(e) => navigate(`${routes.userTicketDetails.navigatePath}/${e.data.id}`)}
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
        >
          <Column className="w-7rem" header="Id" field="prettyId"></Column>
          <Column header="Titlu" field="subject"></Column>
          <Column className="w-12rem" header="Status" field="statusType.name"></Column>
          <Column
            className="w-9rem"
            header="Prioritate"
            body={(rowData: Ticket) => <UrgencyTag value={rowData.urgency}></UrgencyTag>}
          ></Column>
          {userId != null && [
            <Column key="team" className="w-10rem" header="Grupa" field="team.name" />,
            <Column key="team" className="w-10rem" header="Agent" field="agent.displayName" />,
          ]}
          <Column className="w-10rem" header="Data sesizarii" field="createdPretty"></Column>
        </DataTable>
      )}
    </Card>
  );
}
